import { useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";

/* Tick instruments straight from the broker catalog the EA reported. Names are used exactly as
   the broker spelled them — nothing here upper-cases or rewrites a symbol. */
export const InstrumentChecklist = ({
  catalog = [],
  value = [],
  onChange,
  disabled,
  onRefresh,
  refreshing,
  testid = "instrument-checklist",
  emptyHint = "No broker catalog yet — connect an EA and it will report its symbols.",
}) => {
  const [q, setQ] = useState("");
  const picked = useMemo(() => new Set(value), [value]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? catalog.filter((n) => n.toLowerCase().includes(needle)) : catalog;
    return list.slice(0, 300);
  }, [catalog, q]);

  const toggle = (name) => {
    if (disabled) return;
    onChange(picked.has(name) ? value.filter((v) => v !== name) : [...value, name]);
  };

  // A stored name that is no longer in the catalog (broker renamed it, or it predates the catalog).
  const orphans = value.filter((v) => !catalog.includes(v));

  return (
    <div data-testid={testid} className={disabled ? "opacity-50" : ""}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            data-testid={`${testid}-search`}
            disabled={disabled}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${catalog.length} broker symbols`}
            className="w-full rounded-sm bg-zinc-900/60 border border-zinc-800 pl-8 pr-3 py-2 font-mono text-xs text-zinc-100 focus:border-emerald-500 outline-none transition-colors duration-200"
          />
        </div>
        <span className="font-mono text-[11px] text-zinc-400" data-testid={`${testid}-count`}>
          {value.length} selected
        </span>
        {onRefresh && (
          <button
            type="button"
            disabled={disabled || refreshing}
            data-testid={`${testid}-refresh`}
            onClick={onRefresh}
            title="Ask the EA to re-read its MT5 Market Watch (needs EA v2.13+)"
            className="flex items-center gap-1.5 rounded-sm border border-zinc-700 px-2 py-1.5 font-mono text-[11px] text-zinc-300 hover:bg-zinc-800 hover:text-white disabled:opacity-40 transition-colors duration-200"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Asking MT5…" : "Refresh from MT5"}
          </button>
        )}
        {value.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testid}-clear`}
            onClick={() => onChange([])}
            className="font-mono text-[11px] text-zinc-400 hover:text-red-300 transition-colors duration-200"
          >
            Clear all
          </button>
        )}
      </div>

      {catalog.length === 0 ? (
        <p
          className="mt-2 border border-zinc-800 bg-zinc-900/40 px-3 py-4 font-mono text-[11px] text-zinc-400"
          data-testid={`${testid}-empty`}
        >
          {emptyHint}
        </p>
      ) : (
        <div className="mt-2 max-h-64 overflow-y-auto border border-zinc-800 bg-zinc-900/40 p-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
          {shown.map((name) => {
            const on = picked.has(name);
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                data-testid={`${testid}-item-${name}`}
                aria-pressed={on}
                onClick={() => toggle(name)}
                className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-left font-mono text-[11px] transition-colors duration-200 ${
                  on
                    ? "bg-emerald-500/15 text-emerald-200"
                    : "text-zinc-300 hover:bg-zinc-800/70"
                }`}
              >
                <span
                  className={`h-3 w-3 shrink-0 rounded-[2px] border ${
                    on ? "border-emerald-400 bg-emerald-500" : "border-zinc-600"
                  }`}
                />
                <span className="truncate">{name}</span>
              </button>
            );
          })}
          {shown.length === 0 && (
            <p className="col-span-full px-2 py-3 font-mono text-[11px] text-zinc-500">
              Nothing matches “{q}”.
            </p>
          )}
        </div>
      )}

      {orphans.length > 0 && (
        <p
          className="mt-2 font-mono text-[11px] text-amber-300"
          data-testid={`${testid}-orphans`}
        >
          Not in the broker catalog: {orphans.join(", ")} — untick and re-pick to be safe.
        </p>
      )}
    </div>
  );
};
